import { DownloadOutlined, MailOutlined, ReloadOutlined } from '@ant-design/icons';
import { Alert, AutoComplete, Button, Checkbox, DatePicker, Space, Table, Tag, Tooltip, notification } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import PageTitle from 'components/page-title';
import { useCurrentRoles } from 'hooks/use-current-roles';
import { extractErrorMessage, parseFilename, triggerDownload } from 'pages/attendance-manager/component/export-all-modal';
import NoAccess from 'pages/online-reward/components/no-access';
import { API } from 'service/api-route';
import AxiosUtils from 'utils/axios-utils';
import AgencyDrawer from './components/agency-drawer';
import { AgencyFeeMonth, AgencyRow } from './types';
import { currentUtcMonth, emailOptions, monthLabel, num, previousUtcMonth, rewardErrorMessage, viewOf, warningTexts } from './utils';
import styles from './style.module.scss';

const isMonth = (v: string | null): v is string => !!v && /^\d{4}-(0[1-9]|1[0-2])$/.test(v);

/** Phí Agency → Bảng phí tháng (docs/phi-agency/01-cong-thuc-phi-agency.md). Chỉ Admin. */
const AgencyFeePage = () => {
  const { loaded, isAdmin } = useCurrentRoles();
  const [params, setParams] = useSearchParams();
  const month = isMonth(params.get('month')) ? (params.get('month') as string) : previousUtcMonth();
  const agencyKey = params.get('agency');
  const [data, setData] = useState<AgencyFeeMonth | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [showInternal, setShowInternal] = useState(false);

  const setParam = useCallback(
    (key: string, value: string | null) => {
      const next = new URLSearchParams(params);
      if (value) next.set(key, value);
      else next.delete(key);
      setParams(next, { replace: true });
    },
    [params, setParams]
  );

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    AxiosUtils.get(`${API.agencyFeeMonths}/${month}`, undefined, undefined, { timeout: 120_000 })
      .then((res: any) => setData(res?.data ?? null))
      .catch((err) => {
        setData(null);
        setError(rewardErrorMessage(err));
      })
      .finally(() => setLoading(false));
  }, [month]);

  useEffect(() => {
    if (isAdmin) load();
  }, [isAdmin, load]);

  const view = useMemo(() => (data ? viewOf(data, email, showInternal || !!email.trim()) : null), [data, email, showInternal]);
  const emails = useMemo(() => (data ? emailOptions(data.creators) : []), [data]);
  // Đang lọc email thì số trong ngăn kéo theo đúng phần đã lọc (view), không thì lấy cả Agency nội bộ đang ẩn.
  const openAgency = useMemo(
    () => view?.agencies.find((a) => a.key === agencyKey) ?? (email ? null : data?.agencies.find((a) => a.key === agencyKey)) ?? null,
    [view, data, agencyKey, email]
  );
  const drawerCreators = useMemo(() => {
    if (!data) return [];
    const filter = email.trim().toLowerCase();
    return filter ? data.creators.filter((c) => (c.agentEmail || '').toLowerCase() === filter) : data.creators;
  }, [data, email]);

  const exportXlsx = async () => {
    setBusy(true);
    try {
      const response: any = await AxiosUtils.get(`${API.agencyFeeMonths}/${month}/export`, undefined, undefined, {
        responseType: 'blob',
        timeout: 120_000,
      });
      const filename = parseFilename(response?.headers?.['content-disposition'], `phi-agency-${month}.xlsx`);
      triggerDownload(
        new Blob([response.data], {
          type: response?.headers?.['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }),
        filename
      );
    } catch (err) {
      notification.error({ message: 'Không xuất được file', description: await extractErrorMessage(err) });
    } finally {
      setBusy(false);
    }
  };

  if (loaded && !isAdmin) return <NoAccess adminOnly />;

  const grandTotal = view?.totals.total || 0;
  const money = (cls: string) => (v: number) => (v ? <span className={cls}>{num(v)}</span> : <span className={styles.zero}>0</span>);
  const columns: ColumnsType<AgencyRow> = [
    { title: 'STT', key: 'stt', width: 44, align: 'center', render: (_, __, i) => i + 1 },
    {
      title: 'Agency', key: 'agency', width: 168, fixed: 'left',
      render: (_, a) => (
        <span className={styles.agency} title={a.name}>
          <strong>{a.shortName}</strong>
          {a.excluded ? (
            <Tag>Nội bộ</Tag>
          ) : (
            <span className={styles.share}>
              ({grandTotal ? ((a.total / grandTotal) * 100).toFixed(1) : '0'}%){a.branchOf ? ` · nhánh của ${a.branchOf.split('|')[0].trim()}` : ''}
            </span>
          )}
        </span>
      ),
    },
    { title: 'Creator', dataIndex: 'creators', width: 72, align: 'center' },
    { title: 'Tổng KC', dataIndex: 'kc', width: 100, align: 'right', render: (v) => <span className={styles.money}>{num(v)}</span>, sorter: (a, b) => a.kc - b.kc },
    { title: 'Phí NV A', dataIndex: 'a', width: 104, align: 'right', render: money(styles.fee) },
    { title: 'Phí NV B', dataIndex: 'b', width: 96, align: 'right', render: money(styles.fee) },
    { title: 'Phí NV C', dataIndex: 'c', width: 96, align: 'right', render: money(styles.fee) },
    {
      title: <Tooltip title="Phí cố định Creator mới theo bậc và tháng thứ 1 / 2 / 3 (không cộng % thu nhập).">Phí NV D</Tooltip>,
      dataIndex: 'd', width: 108, align: 'right', render: money(styles.fee),
    },
    {
      title: <Tooltip title="Phần Agency nhận. Dòng nhỏ: phần công ty giữ lại của Creator kiểu Chia phí.">Tổng phí Agency</Tooltip>,
      key: 'fee', width: 118, align: 'right', sorter: (a, b) => a.fee - b.fee,
      render: (_, a) => (
        <div className={styles.dCell}>
          {money(styles.feeTotal)(a.fee)}
          {a.companyKeep > 0 && <span className={styles.keep}>Cty giữ {num(a.companyKeep)}</span>}
        </div>
      ),
    },
    {
      title: <Tooltip title="Thưởng Online của Creator. Dòng nhỏ: phần công ty trả thẳng cho Creator đặc biệt (không qua Agency).">Thưởng Creator</Tooltip>,
      key: 'creatorReward', width: 100, align: 'right',
      render: (_, a) => (
        <div className={styles.dCell}>
          {money(styles.creatorReward)(a.creatorReward)}
          {a.creatorDirect > 0 && <span className={styles.direct}>Cty trả {num(a.creatorDirect)}</span>}
        </div>
      ),
    },
    {
      title: <Tooltip title="Số tiền chuyển cho Agency = phí Agency nhận + thưởng Creator Agency phải chuyển lại. Không có Creator đặc biệt thì bằng Tổng TT của bảng cũ.">Chuyển cho Agency</Tooltip>,
      dataIndex: 'toAgency', width: 114, align: 'right', render: money(styles.grand), sorter: (a, b) => a.toAgency - b.toAgency,
    },
  ];

  const warnings = data ? warningTexts(data) : [];
  const t = view?.totals;
  return (
    <>
      <PageTitle
        title="Bảng phí tháng"
        extra={
          <Space wrap>
            <DatePicker
              picker="month"
              allowClear={false}
              format="MM/YYYY"
              value={dayjs(`${month}-01`)}
              disabledDate={(d) => d.format('YYYY-MM') > currentUtcMonth()}
              onChange={(d) => d && setParam('month', d.format('YYYY-MM'))}
            />
            <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Tải lại</Button>
            <Button icon={<DownloadOutlined />} onClick={exportXlsx} disabled={!data} loading={busy}>Xuất Excel</Button>
          </Space>
        }
      />
      {error && <Alert className={styles.section} type="error" showIcon message="Chưa tải được bảng phí" description={error} />}
      {data && view && t && (
        <>
          <div className={styles.statusLine}>
            <Tag color="blue">Tạm tính</Tag>
            <span>Chính sách {data.policy.id ? `#${data.policy.id}` : 'mặc định 2026'} · hiệu lực từ {monthLabel(data.policy.effectiveMonth)}</span>
            {data.lastDataDay && <span>Số liệu tới ngày {data.lastDataDay.slice(8)}/{data.lastDataDay.slice(5, 7)}</span>}
            <span>Thưởng Creator lấy từ Thưởng Online</span>
          </div>
          {warnings.length > 0 && (
            <Alert className={styles.warnings} type="warning" showIcon message="Lưu ý dữ liệu" description={<ul>{warnings.map((w) => <li key={w}>{w}</li>)}</ul>} />
          )}
          <div className={styles.tiles}>
            <div className={`${styles.tile} ${styles.tileKc}`}>
              <div className={styles.tileLabel}>Tổng kim cương</div>
              <div className={styles.tileValue}>{num(t.kc)}</div>
              <div className={styles.tileHint}>{email ? 'Theo email đang lọc' : 'Tất cả Agency'}</div>
            </div>
            <div className={`${styles.tile} ${styles.tileFee}`}>
              <div className={styles.tileLabel}>Tổng phí Agency dự toán</div>
              <div className={styles.tileValue}>{num(t.fee)} đ</div>
              <div className={styles.tileHint}>Phần Agency nhận{t.companyKeep > 0 ? ` · công ty giữ lại ${num(t.companyKeep)} đ` : ''}</div>
            </div>
            <div className={`${styles.tile} ${styles.tileCreator}`}>
              <div className={styles.tileLabel}>Tổng thưởng Creator</div>
              <div className={styles.tileValue}>{num(t.creatorReward)} đ</div>
              <div className={styles.tileHint}>Phần Creator nhận{t.creatorDirect > 0 ? ` · công ty trả thẳng ${num(t.creatorDirect)} đ` : ''}</div>
            </div>
            <div className={`${styles.tile} ${styles.tileTotal}`}>
              <div className={styles.tileLabel}>Tổng thanh toán</div>
              <div className={styles.tileValue}>{num(t.total)} đ</div>
              <div className={styles.tileHint}>
                {t.creatorDirect > 0 ? `Chuyển Agency ${num(t.toAgency)} · trả thẳng Creator ${num(t.creatorDirect)}` : 'Agency + Creator'}
              </div>
            </div>
          </div>
          <div className={styles.filters}>
            <span className={styles.filterLabel}><MailOutlined /> Lọc theo Email Đại diện</span>
            <AutoComplete
              allowClear
              value={email}
              options={emails}
              onChange={(v) => setEmail(v || '')}
              filterOption={(input, option) => String(option?.value || '').includes(input.trim().toLowerCase())}
              placeholder="Ví dụ: daidien@gmail.com → chỉ hiện Creator thuộc email này"
              style={{ width: 380 }}
            />
            <Checkbox checked={showInternal || !!email.trim()} disabled={!!email.trim()} onChange={(e) => setShowInternal(e.target.checked)}>
              Hiện Agency nội bộ
            </Checkbox>
            {!!email.trim() && <span className={styles.muted}>Đang lọc: {num(view?.agencies.reduce((s, a) => s + a.creators, 0))} Creator khớp email (Agency nội bộ vẫn không cộng vào tổng)</span>}
          </div>
          <div className={styles.tableHead}>
            <span className={styles.tableTitle}>Chi tiết theo từng Agency <span className={styles.muted}>(đơn vị: đ)</span></span>
            <span className={styles.muted}>
              {view.agencies.filter((a) => !a.excluded).length} Agency
              {data.counts.excludedAgencies ? ` · ${data.counts.excludedAgencies} nội bộ (không tính tổng)` : ''} · bấm một dòng để xem từng Creator
            </span>
          </div>
          <Table
            rowKey="key"
            size="small"
            loading={loading}
            columns={columns}
            dataSource={view.agencies}
            scroll={{ x: 1120 }}
            pagination={false}
            onRow={(a) => ({ onClick: () => setParam('agency', a.key), className: `${styles.clickable} ${a.excluded ? styles.internalRow : ''}` })}
            summary={() => (
              <Table.Summary fixed>
                <Table.Summary.Row className={styles.summary}>
                  <Table.Summary.Cell index={0} />
                  <Table.Summary.Cell index={1}>{view.agencies.some((a) => a.excluded) ? 'TỔNG (không gồm nội bộ)' : 'TỔNG CỘNG'}</Table.Summary.Cell>
                  <Table.Summary.Cell index={2} align="center">{num(t.creators)}</Table.Summary.Cell>
                  <Table.Summary.Cell index={3} align="right">{num(t.kc)}</Table.Summary.Cell>
                  <Table.Summary.Cell index={4} align="right">{num(t.a)}</Table.Summary.Cell>
                  <Table.Summary.Cell index={5} align="right">{num(t.b)}</Table.Summary.Cell>
                  <Table.Summary.Cell index={6} align="right">{num(t.c)}</Table.Summary.Cell>
                  <Table.Summary.Cell index={7} align="right">{num(t.d)}</Table.Summary.Cell>
                  <Table.Summary.Cell index={8} align="right">
                    <div className={styles.dCell}>
                      {num(t.fee)}
                      {t.companyKeep > 0 && <span className={styles.keep}>Cty giữ {num(t.companyKeep)}</span>}
                    </div>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={9} align="right">
                    <div className={styles.dCell}>
                      {num(t.creatorReward)}
                      {t.creatorDirect > 0 && <span className={styles.direct}>Cty trả {num(t.creatorDirect)}</span>}
                    </div>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={10} align="right">{num(t.toAgency)}</Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            )}
          />
        </>
      )}
      <AgencyDrawer agency={openAgency} creators={drawerCreators} month={month} onClose={() => setParam('agency', null)} />
    </>
  );
};

export default AgencyFeePage;
